import Graphic from "@arcgis/core/Graphic.js";
import Map from "@arcgis/core/Map.js";
import Extent from "@arcgis/core/geometry/Extent.js";
import Point from "@arcgis/core/geometry/Point.js";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer.js";
import LabelClass from "@arcgis/core/layers/support/LabelClass.js";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import TextSymbol from "@arcgis/core/symbols/TextSymbol.js";
import MapView from "@arcgis/core/views/MapView.js";
import { sum } from "ramda";
import { useEffect, useMemo, useRef } from "react";
import { MapSite } from "../../types/map";
import { SiteCoordinates } from "../../types/site";
import { ColorContext, useSitesColors } from "../SiteColorsProvider/SiteColorsProvider";

export function DashboardMapView({ mapSites, colorContext }: { mapSites: MapSite[]; colorContext: ColorContext }) {
  const mapDiv = useRef(null);
  const sites = useMemo(() => mapSites.map((x) => x.siteName), [mapSites]);
  const sitesColors = useSitesColors(sites, colorContext);

  useEffect(() => {
    if (mapDiv.current !== null) {
      setupMap(mapDiv.current, mapSites, sitesColors);
    }
  }, [mapDiv, mapSites, sitesColors]);

  return <div ref={mapDiv} style={{ width: "100%", height: "500px" }}></div>;
}

function setupMap(mapDiv: never, mapSites: MapSite[], sitesColors: Record<string, string>) {
  const map = new Map({
    basemap: "streets-vector",
  });

  const view = new MapView({
    container: mapDiv,
    map: map,
    center: getCenter(mapSites),
  });

  mapSites.forEach((site) => {
    view.graphics.add(createSiteGraphic(site, sitesColors[site.siteName]));
  });

  const labelsLayer = new FeatureLayer({
    source: mapSites.map((site) => createLabelGraphic(site)),
    fields: [
      { name: "ObjectID", alias: "ObjectID", type: "oid" },
      {
        name: "Name",
        alias: "Name",
        type: "string",
      },
    ],
    objectIdField: "ObjectID",
    geometryType: "point",
    renderer: {
      type: "simple",
      symbol: new SimpleMarkerSymbol({
        color: "white",
        size: "0px",
      }),
    } as any,
    labelingInfo: [
      new LabelClass({
        labelExpressionInfo: { expression: "$feature.Name" },
        symbol: new TextSymbol({
          color: "black",
          haloColor: "white",
          haloSize: "2px",
          font: { size: 10, weight: "bold" },
          yoffset: 4,
        }),
      }),
    ],
  });

  view.map.add(labelsLayer);

  if (mapSites.length === 1) {
    view.zoom = 14;
  } else {
    view.extent = getMaxExtent(view.graphics);
  }

  view.on("pointer-move", function (event: any) {
    view.hitTest(event, { exclude: [labelsLayer] }).then(function (response) {
      const hit = response.results[0];
      if (hit && hit.type === "graphic" && hit.layer === null) {
        if (!isSiteGraphic(hit)) return;

        openSitePopup(hit, view);
      }
    });
  });
}

function getCenter(sites: MapSite[]) {
  const sitesCoordinates = sites.map((x) => x.coordinates).filter((x) => x !== null) as SiteCoordinates[];

  const longitudes = sitesCoordinates.map((x) => x.longitude);
  const latitudes = sitesCoordinates.map((x) => x.latitude);

  return [sum(longitudes) / longitudes.length, sum(latitudes) / latitudes.length];
}

function createSiteGraphic(mapSite: MapSite, color: string) {
  const graphic = new Graphic({
    geometry: new Point({
      latitude: mapSite.coordinates.latitude,
      longitude: mapSite.coordinates.longitude,
    }),
    symbol: {
      type: "simple-marker",
      color,
      size: "12px",
    } as any,
    attributes: { site: mapSite },
  });
  return graphic;
}

function getMaxExtent(graphics: __esri.Collection<__esri.Graphic>) {
  const e = graphics
    .map((g) => g.geometry)
    .reduce(
      (acc, geom: any) => ({
        xmin: Math.min(acc.xmin, geom.longitude),
        ymin: Math.min(acc.ymin, geom.latitude),
        xmax: Math.max(acc.xmax, geom.longitude),
        ymax: Math.max(acc.ymax, geom.latitude),
      }),
      {
        xmin: Number.MAX_SAFE_INTEGER,
        ymin: Number.MAX_SAFE_INTEGER,
        xmax: Number.MIN_SAFE_INTEGER,
        ymax: Number.MIN_SAFE_INTEGER,
      },
    );
  return new Extent(e).expand(1.3);
}

function isSiteGraphic(hit: __esri.GraphicHit) {
  return Object.prototype.hasOwnProperty.call(hit.graphic.attributes, "site");
}

function openSitePopup(hit: __esri.GraphicHit, view: MapView) {
  const { siteMean, siteMin, siteMax } = hit.graphic.attributes.site;

  let content = `<li>Mean: ${siteMean.toFixed(2)}</li>
  <li>Min: ${siteMin.toFixed(2)}</li>
  <li>Max: ${siteMax.toFixed(2)}</li>`;

  if (hit.graphic.attributes.site.locationDescription)
    content = `<p>${hit.graphic.attributes.site.locationDescription}</p>` + content;

  view.openPopup({
    title: hit.graphic.attributes.site.siteName,
    location: new Point({
      latitude: hit.graphic.attributes.site.coordinates.latitude,
      longitude: hit.graphic.attributes.site.coordinates.longitude,
    }),
    content,
  });
}

function createLabelGraphic(mapSite: MapSite) {
  return new Graphic({
    geometry: new Point({
      latitude: mapSite.coordinates.latitude,
      longitude: mapSite.coordinates.longitude,
    }),
    attributes: { Name: mapSite.siteName },
  });
}
